import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface LocationData {
  latitude: number;
  longitude: number;
  city: string;
  country: string;
  count: number;
}

interface GeographyMapProps {
  locations: LocationData[];
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || '';

export function GeographyMap({ locations }: GeographyMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current || !MAPBOX_TOKEN) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [0, 20],
      zoom: 1.5,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add markers for each location
    locations.forEach((location) => {
      if (location.latitude && location.longitude) {
        // Create a custom marker
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.style.width = `${Math.max(20, Math.min(60, location.count * 5))}px`;
        el.style.height = `${Math.max(20, Math.min(60, location.count * 5))}px`;
        el.style.backgroundColor = 'rgba(59, 130, 246, 0.6)';
        el.style.borderRadius = '50%';
        el.style.border = '2px solid rgba(59, 130, 246, 1)';
        el.style.cursor = 'pointer';

        new mapboxgl.Marker(el)
          .setLngLat([location.longitude, location.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<div class="p-2">
                <h3 class="font-semibold">${location.city}, ${location.country}</h3>
                <p class="text-sm text-muted-foreground">${location.count} session${location.count > 1 ? 's' : ''}</p>
              </div>`
            )
          )
          .addTo(map.current!);
      }
    });

    return () => {
      map.current?.remove();
    };
  }, [locations]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-muted rounded-lg">
        <p className="text-muted-foreground">
          Mapbox token not configured. Add VITE_MAPBOX_PUBLIC_TOKEN to enable map.
        </p>
      </div>
    );
  }

  return <div ref={mapContainer} className="h-[400px] rounded-lg" />;
}
